import { useState } from 'react';
import { Badge, Text, Tooltip, Group, Box, ActionIcon, Modal } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { Maximize2 } from 'lucide-react';

type QuestionType = 'scale' | 'text' | 'choices' | 'yesNo';

interface SurveyResponsePillProps {
  value: string | string[];
  questionType: QuestionType;
}

// 5-tier color mapping for scale responses
const SCALE_TIER_COLORS: Record<string, { bg: string; text: string; tier: number }> = {
  // Satisfaction scale
  'very satisfied': { bg: '#28463A', text: '#fff', tier: 1 },
  satisfied: { bg: '#4A7C59', text: '#fff', tier: 2 },
  'somewhat satisfied': { bg: '#E9A23B', text: '#fff', tier: 3 },
  neutral: { bg: '#E9A23B', text: '#fff', tier: 3 },
  dissatisfied: { bg: '#E07B4C', text: '#fff', tier: 4 },
  'very dissatisfied': { bg: '#D64545', text: '#fff', tier: 5 },

  // Agreement scale
  'strongly agree': { bg: '#28463A', text: '#fff', tier: 1 },
  agree: { bg: '#4A7C59', text: '#fff', tier: 2 },
  disagree: { bg: '#E07B4C', text: '#fff', tier: 4 },
  'strongly disagree': { bg: '#D64545', text: '#fff', tier: 5 },

  // Numeric ratings
  '5': { bg: '#28463A', text: '#fff', tier: 1 },
  '4': { bg: '#4A7C59', text: '#fff', tier: 2 },
  '3': { bg: '#E9A23B', text: '#fff', tier: 3 },
  '2': { bg: '#E07B4C', text: '#fff', tier: 4 },
  '1': { bg: '#D64545', text: '#fff', tier: 5 },
};

function getScaleColor(value: string): { bg: string; text: string } | null {
  const lower = value.toLowerCase().trim();
  return SCALE_TIER_COLORS[lower] || null;
}

function ScalePill({ value }: { value: string }) {
  const color = getScaleColor(value);
  if (!color) {
    return (
      <Badge
        size="sm"
        variant="light"
        color="gray"
        styles={{ label: { fontSize: 11, letterSpacing: 0.3, textTransform: 'none' } }}
      >
        {value}
      </Badge>
    );
  }

  return (
    <Badge
      size="sm"
      styles={{
        root: { backgroundColor: color.bg },
        label: { color: color.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
      }}
    >
      {value}
    </Badge>
  );
}

function ChoicesPill({ value }: { value: string | string[] }) {
  const values = Array.isArray(value) ? value : [value];

  return (
    <Group gap={4} wrap="wrap">
      {values.map((v, i) => (
        <Badge
          key={i}
          size="sm"
          variant="light"
          color="blue"
          styles={{ label: { fontSize: 11, letterSpacing: 0.3, textTransform: 'none' } }}
        >
          {v}
        </Badge>
      ))}
    </Group>
  );
}

function ExpandableTextCell({ value }: { value: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useClickOutside(() => setIsExpanded(false));

  if (!isExpanded) {
    return (
      <Box
        onClick={() => setIsExpanded(true)}
        style={{
          cursor: 'pointer',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
          whiteSpace: 'normal',
          lineHeight: 1.5,
        }}
      >
        <Text size="sm">{value}</Text>
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={ref}
        style={{
          position: 'relative',
          outline: '2px solid #228BE6',
          outlineOffset: 1,
          borderRadius: 4,
          padding: '4px 32px 4px 6px',
          whiteSpace: 'normal',
          lineHeight: 1.5,
          backgroundColor: '#fff',
          zIndex: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Text size="sm">{value}</Text>
        <Tooltip label="Expand cell" withArrow position="top">
          <ActionIcon
            size="sm"
            variant="filled"
            color="blue"
            style={{ position: 'absolute', top: 4, right: 4 }}
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
          >
            <Maximize2 size={12} />
          </ActionIcon>
        </Tooltip>
      </Box>
      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsExpanded(false);
        }}
        title="Survey Response"
        size="lg"
        withinPortal
      >
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {value}
        </Text>
      </Modal>
    </>
  );
}

export function SurveyResponsePill({ value, questionType }: SurveyResponsePillProps) {
  if (value === null || value === undefined) {
    return <Text size="xs" c="dimmed">—</Text>;
  }

  const displayValue = Array.isArray(value) ? value.join(', ') : value;
  if (!displayValue) {
    return <Text size="xs" c="dimmed">—</Text>;
  }

  switch (questionType) {
    case 'scale':
      return <ScalePill value={displayValue} />;
    case 'choices':
      return <ChoicesPill value={value} />;
    case 'yesNo':
      return <ScalePill value={displayValue} />;
    case 'text':
    default:
      return <ExpandableTextCell value={displayValue} />;
  }
}
